import { useMemo, useState, useCallback, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import classNames from 'classnames';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Tooltip, Legend } from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { usePopper } from 'react-popper';
import { Panel } from '@crazyegginc/hatch';

import { useContainerWidth } from '/src/hooks';
import { SkeletonChart } from '/src/components/SkeletonChart';
import { abbreviateNumber } from '/src/utils';
import { useFunnelEditorContext } from '../../funnel-editor-context';
//import { formatCurrency } from '/src/utils';
import { round } from '/src/utils/math';
import { ENTER_SITE } from '../../constants';

import { ReactComponent as ArrowIcon } from '@crazyegginc/hatch/dist/images/icon-arrow-circle-filled.svg';
import { ReactComponent as TrendIcon } from '@crazyegginc/hatch/dist/images/icon-chart-line.svg';
//import { ReactComponent as MoneyIcon } from '@crazyegginc/hatch/dist/images/icon-currency-outline.svg';
//import { ReactComponent as CartIcon } from '@crazyegginc/hatch/dist/images/icon-cart-outline.svg';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

const BW_MAX = 160;
const FONT_FAMILY = 'proxima-nova';
const RADIUS = { topLeft: 5, topRight: 5, bottomLeft: 0, bottomRight: 0 };

// Plugin to calculate the position of the html "completion box"
const completionAnchor = {
  id: 'completionAnchor',
  afterDatasetsDraw(chart, _args, opts) {
    const { datasetIndex = 0, realCount = 0, gapToBar = 25, boxW = 120, boxH = 47, onChange } = opts || {};
    if (!onChange || realCount < 2) return;

    const x = chart.scales?.x;
    const area = chart.chartArea;
    const meta = chart.getDatasetMeta(datasetIndex);
    const idx = realCount - 1;
    const el = meta?.data?.[idx];
    if (!x || !area || !el) return;

    const { x: cx, width } = el.getProps(['x', 'width'], false);
    const rightEdge = cx + (width ? width / 2 : 0);

    const left = Math.min(rightEdge + gapToBar, area.right - boxW - 8);
    const top = area.bottom - boxH - 0;

    // cache last payload on the chart instance to avoid duplicate emits in the same frame
    const key = '_completionAnchor_last';
    const prev = chart[key];
    const next = { left, top, boxW, boxH };
    if (
      !prev ||
      Math.abs(prev.left - left) > 0.5 ||
      Math.abs(prev.top - top) > 0.5 ||
      prev.boxW !== boxW ||
      prev.boxH !== boxH
    ) {
      chart[key] = next;
      onChange(next);
    }
  },
};

const pctFmt = (a, b) => {
  if (!Number.isFinite(a) || !Number.isFinite(b) || a <= 0) return null;
  return `${round((b / a) * 100, 1)}%`;
};

function drawArrowIcon(ctx, cx, cy, r, color = '#fff') {
  const shaftHalf = r * 0.1; // half thickness of the shaft
  const shaftLeft = cx - r * 0.55; // left end of shaft
  const headBaseX = cx + r * 0.1; // where chevron starts
  const tipX = cx + r * 0.55; // arrow tip
  const headHalfH = shaftHalf * 4.5; // chevron height from center

  ctx.save();
  ctx.strokeStyle = color;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';

  // Shaft (all the way to the chevron base)
  ctx.lineWidth = shaftHalf * 2;
  ctx.beginPath();
  ctx.moveTo(shaftLeft, cy);
  ctx.lineTo(tipX, cy);
  ctx.stroke();

  // Chevron head (two lines forming ">")
  ctx.beginPath();
  ctx.moveTo(headBaseX, cy - headHalfH);
  ctx.lineTo(tipX, cy);
  ctx.lineTo(headBaseX, cy + headHalfH);
  ctx.stroke();

  ctx.restore();
}

// Plugin that draws the circled arrow and the conversion rate between bars
const betweenBarsArrows = {
  id: 'betweenBarsArrows',
  afterDatasetsDraw(chart, _args, opts) {
    const { ctx, chartArea, scales } = chart;
    const x = scales.x;
    if (!x || !chartArea) return;

    const {
      isDummy = [],
      datasetIndex = 0,
      circleR = 12,
      circleFill = '#A5ACBC',
      arrowColor = '#fff',
      pctColor = '#0A1C2E',
      pctFont = { size: 14, weight: '600', family: FONT_FAMILY },
      gapYOffset = 35, // distance from chart bottom to circle center
      pctGapY = 5, // distance from circle bottom to percent text
    } = opts || {};

    const ds = chart.data.datasets[datasetIndex];
    if (!ds) return;

    // vertical positions
    const cy = chartArea.bottom - gapYOffset; // circle center Y
    const ty = cy + circleR + pctGapY; // percent text baseline

    ctx.save();
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.font = `${pctFont.weight} ${pctFont.size}px ${pctFont.family}`;

    const n = chart.data.labels.length;
    if (n <= 1) {
      ctx.restore();
      return;
    }

    for (let i = 0; i < n - 1; i++) {
      // skip if either side is dummy or null/NaN
      const leftIsDummy = isDummy[i];
      const rightIsDummy = isDummy[i + 1];
      const a = ds.data[i];
      const b = ds.data[i + 1];
      if (leftIsDummy || rightIsDummy || a == null || b == null || !isFinite(a) || !isFinite(b)) continue;

      // x-positions: tick centers; arrow centered in the gap
      const x1 = x.getPixelForTick(i);
      const x2 = x.getPixelForTick(i + 1);
      const cx = (x1 + x2) / 2;

      ctx.beginPath();
      ctx.arc(cx, cy, circleR, 0, Math.PI * 2);
      ctx.fillStyle = circleFill;
      ctx.fill();

      ctx.lineWidth = 1;
      ctx.strokeStyle = circleFill;
      ctx.stroke();

      drawArrowIcon(ctx, cx, cy, circleR, arrowColor);

      // percent text (below circle)
      const pct = pctFmt(Number(a), Number(b));
      if (pct) {
        ctx.fillStyle = pctColor;
        ctx.textBaseline = 'top';
        ctx.fillText(pct, cx, ty);
      }
    }

    ctx.restore();
  },
};

const nf = typeof Intl !== 'undefined' ? new Intl.NumberFormat() : { format: (n) => String(n) };
// Plugin that draws the values above the bars
const barValueLabels = {
  id: 'barValueLabels',
  afterDatasetsDraw(chart, _args, opts) {
    const { ctx, chartArea } = chart;
    const {
      isDummy = [],
      color = '#68768D',
      selectedColor = '#0A1C2E',
      selectedIndex = null,
      font = { size: 14, weight: '600', family: FONT_FAMILY },
      offsetY = 3,
    } = opts || {};

    chart.data.datasets.forEach((ds, di) => {
      const meta = chart.getDatasetMeta(di);
      meta.data.forEach((el, i) => {
        ctx.save();
        ctx.fillStyle = selectedIndex === i ? selectedColor : color;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'bottom';
        ctx.font = `${font.weight} ${font.size}px ${font.family}`;
        const v = ds.data[i];
        if (v == null || isDummy[i]) return;

        const { x, y, base } = el.getProps(['x', 'y', 'base'], false);
        const top = Math.min(y, base);
        const tx = x;
        let ty = top - offsetY;

        // if too close to the top edge, nudge inside the plot
        if (ty < chartArea.top + 2) {
          ty = chartArea.top + 2;
        }

        ctx.fillText(nf.format(v), tx, ty);
      });
    });

    ctx.restore();
  },
};

function roundRectPath(ctx, x, y, w, h, r) {
  const tl = r?.topLeft ?? r ?? 0;
  const tr = r?.topRight ?? r ?? 0;
  const br = r?.bottomRight ?? 0;
  const bl = r?.bottomLeft ?? 0;
  ctx.beginPath();
  ctx.moveTo(x + tl, y);
  ctx.lineTo(x + w - tr, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + tr);
  ctx.lineTo(x + w, y + h - br);
  ctx.quadraticCurveTo(x + w, y + h, x + w - br, y + h);
  ctx.lineTo(x + bl, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - bl);
  ctx.lineTo(x, y + tl);
  ctx.quadraticCurveTo(x, y, x + tl, y);
  ctx.closePath();
}

// Plugin that adds a gradient overlay to bars
const barShineOverlay = {
  id: 'barShineOverlay',
  afterDatasetsDraw(chart, _args, opts) {
    const { ctx } = chart;
    const {
      isDummy = [],
      borderRadius = RADIUS,
      colorTop = 'rgba(255,255,255,0.2)',
      colorBottom = 'rgba(0,0,0,0.05)',
    } = opts || {};

    chart.data.datasets.forEach((ds, di) => {
      const meta = chart.getDatasetMeta(di);
      meta.data.forEach((el, i) => {
        if (!el || ds.data[i] == null || isDummy[i]) return;

        // current geometry (no “final” props so hover doesn’t stick)
        const { x, y, base, width } = el.getProps(['x', 'y', 'base', 'width'], false);
        const top = Math.min(y, base);
        const bottom = Math.max(y, base);
        const left = x - width / 2;
        const w = width;
        const h = Math.max(1, bottom - top);

        ctx.save();
        // clip to the same rounded shape as the bar
        roundRectPath(ctx, left, top, w, h, borderRadius);
        ctx.clip();

        // vertical gradient overlay
        const g = ctx.createLinearGradient(0, top, 0, bottom);
        g.addColorStop(0, colorTop);
        g.addColorStop(1, colorBottom);

        ctx.fillStyle = g;
        ctx.fillRect(left, top, w, h);
        ctx.restore();
      });
    });
  },
};

function truncateText(ctx, text, maxWidth) {
  const str = String(text ?? '');
  if (!str) return '';
  if (ctx.measureText(str).width <= maxWidth) return str;
  let lo = 0,
    hi = str.length;
  // binary search to minimize measure calls
  while (lo < hi) {
    const mid = ((lo + hi) >> 1) + 1;
    const candidate = str.slice(0, mid) + '…';
    if (ctx.measureText(candidate).width <= maxWidth) lo = mid;
    else hi = mid - 1;
  }
  return str.slice(0, lo) + '…';
}

const stepLabelsPlugin = {
  id: 'stepLabelsPlugin',
  afterDraw(chart) {
    const { ctx, chartArea, scales } = chart;
    const x = scales.x;
    if (!x) return;

    const labels = chart.data?.labels || [];
    const opts = chart.options?.plugins?.stepLabelsPlugin || {};
    const {
      isDummy = [],
      datasetIndex = 0,
      pillBg = '#A5ACBC',
      selectedPillBg = '#68768D',
      pillText = '#fff',
      labelText = '#68768D',
      selectedLabelText = '#0A1C2E',
      pillRadius = 4,
      pillPadX = 8,
      pillPadY = 4,
      gapPx = 10, // gap between pill and text
      pillFont = { size: 12, weight: '600', family: FONT_FAMILY },
      labelFont = { size: 14, weight: '600', family: FONT_FAMILY },
      selectedIndex = null,
      selectedBg = '#EEF1F6',
      selectedPadY = 11,
      selectedRadius = 6,
    } = opts;

    chart._stepLabelHitRects = [];

    const baselineY = chartArea.bottom + 30; // vertical center for the line

    labels.forEach((label, i) => {
      if (isDummy[i]) {
        chart._stepLabelHitRects[i] = null;
        return;
      }

      const cx = x.getPixelForTick(i);

      // Calculate sizes
      ctx.save();
      ctx.font = `${pillFont.weight} ${pillFont.size}px ${pillFont.family}`;
      const stepStr = String(i + 1);
      const stepW = ctx.measureText(stepStr).width;
      const pillW = Math.round(stepW + pillPadX * 2);
      const pillH = Math.round(pillFont.size + pillPadY * 2);

      ctx.font = `${labelFont.weight} ${labelFont.size}px ${labelFont.family}`;

      let barW = 0;
      const meta = chart.getDatasetMeta(datasetIndex);
      const el = meta?.data?.[i];
      if (el?.getProps) {
        const props = el.getProps(['width'], false);
        barW = Number(props?.width) || 0;
      }

      const truncated = truncateText(ctx, String(label), Math.max(0, barW - (pillW + gapPx * 2)));
      const truncW = ctx.measureText(truncated).width;

      // center the whole unit (pill + gap + label)
      const unitW = pillW + gapPx + truncW;
      const unitX = Math.round(cx - unitW / 2);
      const pillX = unitX;
      const pillY = Math.round(baselineY - pillH / 2);
      const labelX = pillX + pillW + gapPx;

      if (!barW) {
        // fallback estimate from category bandwidth & dataset percentages
        const ds = chart.data.datasets?.[datasetIndex] || {};
        const catPct = ds.categoryPercentage ?? 0.7;
        const barPct = ds.barPercentage ?? 0.9;
        const prevC = i > 0 ? x.getPixelForTick(i - 1) : null;
        const nextC = i < labels.length - 1 ? x.getPixelForTick(i + 1) : null;
        const bw =
          labels.length === 1
            ? chartArea.right - chartArea.left
            : prevC != null && nextC != null
              ? (nextC - prevC) / 2
              : i === 0 && nextC != null
                ? nextC - cx
                : i === labels.length - 1 && prevC != null
                  ? cx - prevC
                  : 0;
        barW = Math.max(1, bw * catPct * barPct);
      }

      // Selected container: match BAR width, not text width
      const isSel = selectedIndex === i;
      if (isSel) {
        const x0 = Math.round(cx - barW / 2);
        const y0 = pillY - selectedPadY;
        const w0 = Math.round(barW);
        const h0 = pillH + selectedPadY * 2;

        ctx.save();
        ctx.fillStyle = selectedBg;
        const r = Math.min(selectedRadius, h0 / 2);
        ctx.beginPath();
        ctx.moveTo(x0 + r, y0);
        ctx.lineTo(x0 + w0 - r, y0);
        ctx.quadraticCurveTo(x0 + w0, y0, x0 + w0, y0 + r);
        ctx.lineTo(x0 + w0, y0 + h0 - r);
        ctx.quadraticCurveTo(x0 + w0, y0 + h0, x0 + w0 - r, y0 + h0);
        ctx.lineTo(x0 + r, y0 + h0);
        ctx.quadraticCurveTo(x0, y0 + h0, x0, y0 + h0 - r);
        ctx.lineTo(x0, y0 + r);
        ctx.quadraticCurveTo(x0, y0, x0 + r, y0);
        ctx.closePath();
        ctx.fill();
        ctx.restore();

        // Update hit area to the container (bar-width)
        chart._stepLabelHitRects[i] = { x: x0, y: y0, w: w0, h: h0 };
      } else {
        // Non-selected: hit area = pill+text only
        chart._stepLabelHitRects[i] = { x: unitX, y: pillY, w: unitW, h: pillH };
      }

      // Draw the Pill
      ctx.fillStyle = selectedIndex === i ? selectedPillBg : pillBg;
      const r = Math.min(pillRadius, pillH / 2, pillW / 2);
      ctx.beginPath();
      ctx.moveTo(pillX + r, pillY);
      ctx.lineTo(pillX + pillW - r, pillY);
      ctx.quadraticCurveTo(pillX + pillW, pillY, pillX + pillW, pillY + r);
      ctx.lineTo(pillX + pillW, pillY + pillH - r);
      ctx.quadraticCurveTo(pillX + pillW, pillY + pillH, pillX + pillW - r, pillY + pillH);
      ctx.lineTo(pillX + r, pillY + pillH);
      ctx.quadraticCurveTo(pillX, pillY + pillH, pillX, pillY + pillH - r);
      ctx.lineTo(pillX, pillY + r);
      ctx.quadraticCurveTo(pillX, pillY, pillX + r, pillY);
      ctx.closePath();
      ctx.fill();

      // Pill number
      ctx.fillStyle = pillText;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.font = `${pillFont.weight} ${pillFont.size}px ${pillFont.family}`;
      ctx.fillText(stepStr, pillX + pillW / 2, pillY + pillH / 2);

      // Draw label (truncated)
      ctx.fillStyle = selectedIndex === i ? selectedLabelText : labelText;
      ctx.textAlign = 'left';
      ctx.textBaseline = 'middle';
      ctx.font = `${labelFont.weight} ${labelFont.size}px ${labelFont.family}`;
      ctx.fillText(truncated, labelX, baselineY);

      ctx.restore();
    });
  },
};

// Plugin that draws a 'background' behind the bar when selected or hovered
const barBackgroundOverlay = {
  id: 'barBackgroundOverlay',
  beforeDatasetsDraw(chart, _args, opts) {
    const {
      selectedIndex,
      hoveredIndex,
      datasetIndex = 0,
      borderRadius = RADIUS,
      color = 'rgba(225, 230, 239, 0.41)',
    } = opts;
    const indexes = [...new Set([selectedIndex, hoveredIndex])].filter((x) => x != null);
    if (indexes.length < 1) return;

    indexes.forEach((index) => {
      const { ctx, chartArea } = chart;
      const meta = chart.getDatasetMeta(datasetIndex);
      const el = meta?.data?.[index];
      if (!el) return;

      // read current geometry (non-final so it follows animations/resize)
      const { x, width } = el.getProps(['x', 'width'], false);

      // fallback if width isn't available yet
      let w = Number.isFinite(width) ? width : 0;
      if (w <= 0) {
        const ds = chart.data.datasets[datasetIndex] || {};
        const catPct = ds.categoryPercentage ?? 0.7;
        const barPct = ds.barPercentage ?? 0.9;
        // estimate from category bandwidth
        const xScale = chart.scales.x;
        const bw = xScale?.getPixelForTick(index + 1) - xScale?.getPixelForTick(index) || 0;
        w = Math.max(1, bw * catPct * barPct);
      }

      const left = Math.round(x - w / 2);
      const top = chartArea.top;
      const height = chartArea.bottom - chartArea.top;

      ctx.save();
      // clip to the same rounded shape as the bar
      roundRectPath(ctx, left, top, w, height, borderRadius);
      ctx.clip();

      ctx.fillStyle = color;
      ctx.fillRect(left, top, Math.round(w), height);
      ctx.restore();
    });
  },
};

// Plugin that makes bars selectable
const selectionController = {
  id: 'selectionController',
  afterEvent(chart, args, opts) {
    const { isDummy = [], onSelect, datasetIndex = 0 } = opts || {};
    if (!onSelect) return;

    const e = args.event;
    const native = e?.native || e;

    const area = chart.chartArea;
    const xScale = chart.scales?.x;
    if (!area || !xScale) return;

    const rect = chart.canvas.getBoundingClientRect();
    const mx = e && typeof e.x === 'number' ? e.x : native?.clientX - rect.left;
    const my = e && typeof e.y === 'number' ? e.y : native?.clientY - rect.top;

    // helper: find bar index whose stripe is hit
    const meta = chart.getDatasetMeta(datasetIndex);
    const labels = chart.data?.labels || [];

    const findBarIndexInStripe = () => {
      if (mx < area.left || mx > area.right) return -1;
      for (let i = 0; i < labels.length; i++) {
        const el = meta?.data?.[i];
        if (!el || isDummy[i]) continue;

        const { x, width, y, base } = el.getProps(['x', 'width', 'y', 'base'], false);
        if (!Number.isFinite(x) || !Number.isFinite(width)) continue;

        const left = x - width / 2;
        const right = x + width / 2;
        if (mx < left || mx > right) continue;

        const barBottom = Math.max(y, base);

        // stripe: from chart top down to bar bottom
        if (my >= area.top && my <= barBottom) {
          return i;
        }
      }
      return -1;
    };

    // pointer cursor on hover
    if (e?.type === 'mousemove') {
      let pointer = false;

      // 1) bar stripe
      const hitIndex = findBarIndexInStripe();
      if (hitIndex >= 0) pointer = true;

      // 2) labels (use stored rects from stepLabelsPlugin)
      if (!pointer) {
        const rects = chart._stepLabelHitRects || [];
        for (let i = 0; i < rects.length; i++) {
          const r = rects[i];
          if (!r || isDummy[i]) continue;
          if (mx >= r.x && mx <= r.x + r.w && my >= r.y && my <= r.y + r.h) {
            pointer = true;
            break;
          }
        }
      }

      chart.canvas.style.cursor = pointer ? 'pointer' : 'default';
    }

    // click: select bar (stripe) or label
    if (e?.type === 'click') {
      // 1) bar stripe
      const hitIndex = findBarIndexInStripe();
      if (hitIndex >= 0) {
        onSelect(hitIndex);
        return;
      }

      // 2) labels
      const rects = chart._stepLabelHitRects || [];
      for (let i = 0; i < rects.length; i++) {
        const r = rects[i];
        if (!r || isDummy[i]) continue;
        if (mx >= r.x && mx <= r.x + r.w && my >= r.y && my <= r.y + r.h) {
          onSelect(i);
          return;
        }
      }
    }
  },
};

// Plugin that emits the info (coordinates, visibility, index) about hovered bars for an external tooltip
const reactTooltipPlugin = {
  id: 'reactTooltipPlugin',
  afterEvent(chart, args, opts) {
    const setTooltipState = opts?.setTooltipState;
    const tooltipPosRef = opts?.tooltipPosRef;
    const isDummy = opts?.isDummy || [];
    const datasetIndex = opts?.datasetIndex ?? 0; // which dataset's bars to read
    if (!setTooltipState || !tooltipPosRef) return;

    const e = args.event;
    const native = e?.native || e;

    // Hide on leave
    if (e?.type === 'mouseout' || e?.type === 'mouseleave') {
      tooltipPosRef.current.visible = false;
      setTooltipState((t) => (t.index == null ? t : { index: null }));
      return;
    }

    const area = chart.chartArea;
    const xScale = chart.scales?.x;
    if (!area || !xScale) return;

    // Mouse coords (canvas-relative)
    const mx = typeof e.x === 'number' ? e.x : native?.clientX - chart.canvas.getBoundingClientRect().left;
    const my = typeof e.y === 'number' ? e.y : native?.clientY - chart.canvas.getBoundingClientRect().top;

    // Outside plot horizontally → clear
    if (mx < area.left || mx > area.right) {
      tooltipPosRef.current.visible = false;
      setTooltipState((t) => (t.index == null ? t : { index: null }));
      return;
    }

    // Find the bar whose horizontal footprint contains mx
    const meta = chart.getDatasetMeta(datasetIndex);
    const labels = chart.data?.labels || [];

    let hitIndex = -1;
    let hitEl = null;

    for (let i = 0; i < labels.length; i++) {
      const el = meta?.data?.[i];
      if (!el || isDummy[i]) continue; // skip dummies / missing

      const { x, width, y, base } = el.getProps(['x', 'width', 'y', 'base'], false);
      if (!Number.isFinite(x) || !Number.isFinite(width)) continue;

      const left = x - width / 2;
      const right = x + width / 2;

      // Horizontal must be within the bar
      if (mx >= left && mx <= right) {
        // Vertical: allow entire stripe ABOVE the bar (down to its bottom)
        const barBottom = Math.max(y, base);

        if (my >= area.top && my <= barBottom) {
          hitIndex = i;
          hitEl = el;
          break;
        }
      }
    }

    if (hitIndex < 0) {
      tooltipPosRef.current.visible = false;
      setTooltipState((t) => (t.index == null ? t : { index: null }));
      return;
    }

    // Anchor position (use element’s tooltipPosition if available)
    const pos = typeof hitEl.tooltipPosition === 'function' ? hitEl.tooltipPosition() : { x: hitEl.x, y: hitEl.y };

    tooltipPosRef.current = { x: pos.x, y: 100, visible: true }; // update: fix tooltip y to top of canvas - 100px;

    // Only change index when it actually changes
    setTooltipState((t) => (t.index === hitIndex ? t : { index: hitIndex }));
  },
};

const EPS = 0.5;
const diffPos = (a, b) =>
  !a ||
  !b ||
  Math.abs(a.left - b.left) > EPS ||
  Math.abs(a.top - b.top) > EPS ||
  a.boxW !== b.boxW ||
  a.boxH !== b.boxH;

export function MainChart({ loading, summary, totalValues, stepsData, currency }) {
  const [wrapRef, width] = useContainerWidth();
  const [completionPos, setCompletionPos] = useState(null);
  const lastPosRef = useRef(null);
  const { selectStep, selectedStep } = useFunnelEditorContext();

  const [tooltipState, setTooltipState] = useState({ index: null });
  const tooltipPosRef = useRef({ x: 0, y: 0 });

  const handleCompletionPos = useCallback((p) => {
    if (diffPos(lastPosRef.current, p)) {
      lastPosRef.current = p;
      requestAnimationFrame(() => setCompletionPos(p));
    }
  }, []);

  const inputData = useMemo(
    () =>
      stepsData.map((s, i) => ({
        id: s.id ?? ENTER_SITE,
        label: s.name,
        values: { all: summary[i], totalValue: totalValues[i] },
      })),
    [stepsData, summary, totalValues],
  );

  const realLabels = useMemo(() => inputData.map((s) => s.label), [inputData]);
  const realCounts = useMemo(() => inputData.map((s) => s.values.all ?? 0), [inputData]);
  const realCount = realLabels.length;

  const { totalSlots, dummyCount, barPx, shouldScroll, innerWidth } = useMemo(
    () => planCategories(width, realCount),
    [width, realCount],
  );

  const labels = useMemo(
    () => [
      ...realLabels,
      ...Array.from({ length: dummyCount }, () => ''), // empty labels for dummies
    ],
    [realLabels, dummyCount],
  );

  const isDummy = useMemo(() => Array.from({ length: totalSlots }, (_, i) => i >= realCount), [totalSlots, realCount]);

  const data = useMemo(
    () => ({
      labels,
      datasets: [
        {
          label: 'All visitors',
          data: labels.map((_, i) => (i < realCounts.length ? realCounts[i] : null)),
          borderRadius: (ctx) => (isDummy[ctx.dataIndex] ? 0 : RADIUS),
          borderSkipped: 'bottom',
          barThickness: barPx,
          maxBarThickness: BW_MAX,
          categoryPercentage: 0.7,
          barPercentage: 0.9,
          backgroundColor: (ctx) => (isDummy[ctx.dataIndex] ? 'rgba(0,0,0,0)' : 'rgb(186,137,245)'),
        },
      ],
    }),
    [labels, realCounts, isDummy, barPx],
  );

  const ySuggestedMax = useMemo(() => Math.max(1, ...realCounts.filter(Number.isFinite)), [realCounts]);
  const options = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      layout: { padding: { top: 28, right: 20, bottom: 56, left: 0 } },
      plugins: {
        legend: { display: false },
        tooltip: {
          enabled: false,
        },
      },
      scales: {
        x: {
          type: 'category',
          offset: true,
          grid: { display: false, offset: true },
          border: { display: false },
          ticks: { display: false },
        },
        y: {
          beginAtZero: true,
          suggestedMax: ySuggestedMax,
          grid: { color: 'rgba(151, 151, 151, 0.2)', drawTicks: false },
          border: {
            display: false,
          },
          ticks: {
            padding: 10,
            callback: (val) => abbreviateNumber(val),
            color: 'rgb(104, 118, 141)',
            maxTicksLimit: 5,
          },
        },
      },
      interaction: { mode: 'nearest', intersect: true },
      animation: { duration: 300 },
    }),
    [ySuggestedMax],
  );

  const completionPluginOpts = useMemo(
    () => ({
      realCount,
      onChange: handleCompletionPos,
    }),
    [realCount, handleCompletionPos],
  );

  const tooltipPluginOpts = useMemo(
    () => ({
      setTooltipState,
      tooltipPosRef,
      isDummy,
    }),
    [setTooltipState, isDummy],
  );

  return (
    <Panel variant="secondary">
      <div className="text-header-2 mb-6">Visitors across your Funnel</div>
      <div
        ref={wrapRef}
        className={classNames('h-[350px] relative w-full', {
          'overflow-auto': shouldScroll,
        })}
      >
        <div
          className="absolute h-full"
          style={{
            width: shouldScroll && typeof innerWidth === 'number' ? `${innerWidth}px` : '100%',
          }}
        >
          {loading ? (
            <SkeletonChart />
          ) : (
            <Bar
              data={data}
              options={{
                ...options,
                plugins: {
                  ...options.plugins,
                  barBackgroundOverlay: { hoveredIndex: tooltipState.index, selectedIndex: selectedStep, isDummy },
                  stepLabelsPlugin: { isDummy, selectedIndex: selectedStep },
                  barValueLabels: { isDummy, selectedIndex: selectedStep },
                  barShineOverlay: { isDummy },
                  betweenBarsArrows: { isDummy },
                  completionAnchor: completionPluginOpts,
                  selectionController: { onSelect: selectStep, isDummy },
                  reactTooltipPlugin: tooltipPluginOpts,
                },
                events: ['mousemove', 'mouseout', 'mouseleave', 'click'],
              }}
              plugins={[
                barBackgroundOverlay,
                stepLabelsPlugin,
                barShineOverlay,
                barValueLabels,
                betweenBarsArrows,
                completionAnchor,
                selectionController,
                reactTooltipPlugin,
              ]}
            />
          )}
        </div>

        {realCount > 1 && completionPos ? (
          <CompletionBox
            left={completionPos.left}
            top={completionPos.top}
            width={completionPos.boxW}
            height={completionPos.boxH}
            first={Number(data.datasets[0].data[0])}
            last={Number(data.datasets[0].data[realCount - 1])}
          />
        ) : null}

        <CustomTooltip
          tooltipPosRef={tooltipPosRef}
          wrapperRef={wrapRef}
          tooltipState={tooltipState}
          title={labels[tooltipState.index]}
          first={Number(data.datasets[0].data[0])}
          prev={tooltipState.index > 0 ? Number(data.datasets[0].data[tooltipState.index - 1]) : null}
          current={Number(data.datasets[0].data[tooltipState.index])}
          totalValue={
            inputData[tooltipState.index]?.id === ENTER_SITE
              ? NaN
              : Number(inputData[tooltipState.index]?.values.totalValue)
          }
          currency={currency}
        />
      </div>
    </Panel>
  );
}

function planCategories(containerWidth, stepsCount) {
  const GAP = 75;
  const BAR_MIN = 100;
  const BAR_MAX = 160;

  const completionSlot = stepsCount > 1 ? 1 : 0; // reserve space at the end for CompletionBox
  const slotsNeeded = Math.max(stepsCount + completionSlot, 1);

  // Unknown width or no steps -> fall back (Chart.js will distribute)
  if (!containerWidth || stepsCount === 0) {
    return {
      barPx: BAR_MAX,
      totalSlots: stepsCount || 1,
      dummyCount: Math.max(0, 1 - stepsCount),
      shouldScroll: false,
      innerWidth: '100%',
    };
  }

  // First try to fit WITHOUT scrolling by solving per-slot space:
  // perSlot = barPx + GAP  =>  barPx = perSlot - GAP
  const perSlot = containerWidth / slotsNeeded;
  let barPx = Math.min(BAR_MAX, Math.max(BAR_MIN, Math.floor(perSlot - GAP)));

  let neededWidth = slotsNeeded * (barPx + GAP);

  if (neededWidth <= containerWidth) {
    // We fit: pad with extra dummy slots so bars appear left-aligned
    // Using the *current* bar width, compute how many full slots fit.
    const totalSlots = Math.max(slotsNeeded, Math.floor(containerWidth / (barPx + GAP)));

    return {
      barPx,
      totalSlots,
      // dummy slots include the trailing completion slot (already counted in slotsNeeded)
      dummyCount: totalSlots - stepsCount,
      shouldScroll: false,
      innerWidth: '100%',
    };
  }

  // We don't fit. Shrink bars to BAR_MIN and check again.
  barPx = BAR_MIN;
  neededWidth = slotsNeeded * (barPx + GAP);

  if (neededWidth <= containerWidth) {
    // With smaller bars we now fit, pad to left-align as above.
    const totalSlots = Math.max(slotsNeeded, Math.floor(containerWidth / (barPx + GAP)));

    return {
      barPx,
      totalSlots,
      dummyCount: totalSlots - stepsCount,
      shouldScroll: false,
      innerWidth: '100%',
    };
  }

  // Still doesn't fit: enable horizontal scroll.
  return {
    barPx,
    totalSlots: slotsNeeded,
    dummyCount: completionSlot,
    shouldScroll: true,
    innerWidth: neededWidth,
  };
}

function CompletionBox({ left, top, width, height, first, last }) {
  const pct = first > 0 && Number.isFinite(last) ? `${((last / first) * 100).toFixed(1)}%` : '—';

  return (
    <div
      className="absolute shadow rounded-[5px] flex flex-col justify-center bg-woodsmoke-500/90 p-2"
      style={{
        left,
        top,
        width,
        height,
      }}
    >
      <div className="text-mystic-500 text-sm">Completion rate:</div>
      <div className="flex items-center space-x-2">
        <span className="rounded-full w-2 h-2 bg-lavender-500" />
        <span className="text-white font-semibold text-sm">{pct}</span>
      </div>
    </div>
  );
}

function CustomTooltip({
  tooltipState,
  tooltipPosRef,
  wrapperRef,
  title,
  first,
  prev,
  current /*totalValue, currency*/,
}) {
  const tooltipLastDrawRef = useRef({ x: NaN, y: NaN });

  const virtualRef = useRef({
    getBoundingClientRect: () => ({
      top: 0,
      left: 0,
      bottom: 0,
      right: 0,
      width: 0,
      height: 0,
    }),
  });

  const [tooltipElement, setTooltipElement] = useState(null);
  const { styles, attributes, update } = usePopper(virtualRef.current, tooltipElement, {
    placement: 'bottom',
    modifiers: [
      {
        name: 'flip',
        options: {
          allowedAutoPlacements: 'bottom',
          fallbackPlacements: 'bottom',
        },
      },
      {
        name: 'preventOverflow',
        options: {
          mainAxis: false,
        },
      },
    ],
  });

  // Tooltip position and visibility updater
  useEffect(() => {
    let raf = 0;
    const tick = () => {
      const { x, y, visible } = tooltipPosRef.current;
      const last = tooltipLastDrawRef.current;
      const { left: wrapperLeft, top: wrapperTop } = wrapperRef.current.getBoundingClientRect();
      const wrapperScrollLeft = wrapperRef.current.scrollLeft;
      const newX = wrapperLeft - wrapperScrollLeft + x + 120;
      const newY = wrapperTop + y + 20;
      if (newX !== last.x || newY !== last.y) {
        virtualRef.current.getBoundingClientRect = () => ({
          top: newY,
          left: newX,
          bottom: 0,
          right: 0,
          width: 0,
          height: 0,
        });
        tooltipLastDrawRef.current = { x: newX, y: newY };
      }
      if (visible !== last.visible) {
        tooltipElement.style.opacity = visible ? '1' : '0';
        last.visible = visible;
      }
      update?.();

      raf = requestAnimationFrame(tick);
    };

    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [tooltipPosRef, wrapperRef, tooltipElement, update]);

  return createPortal(
    <div
      ref={setTooltipElement}
      style={styles.popper}
      {...attributes.popper}
      className={classNames('absolute pointer-events-none transition-opacity')}
    >
      {tooltipState.index != null && (
        <div className="w-[175px] rounded-[5px] border border-mystic-500 bg-white shadow text-xs text-black-pearl-500">
          <div className="font-semibold px-2.5 pt-1.5 mb-1.5 line-clamp-2">{title}</div>
          <div className="h-px border-b border-dashed border-mystic-500 w-full" />
          <div className="px-2.5">
            {tooltipState.index > 0 && (
              <TooltipRow
                icon={ArrowIcon}
                iconClassName="rotate-180"
                value={`${(prev - current).toLocaleString()} (${prev ? round(100 - (current / prev) * 100, 1) : 0}%)`}
                text="visitors dropped-off"
              />
            )}

            <TooltipRow
              icon={TrendIcon}
              value={`${first ? round((current / first) * 100, 1) : 0}%`}
              text="of all visitors made it to this step"
            />
            {/*isNaN(totalValue) ? null : (
              <>
                <TooltipRow icon={MoneyIcon} value={formatCurrency(totalValue / 10000, currency)} text="total value" />
                <TooltipRow
                  icon={CartIcon}
                  value={formatCurrency(current ? totalValue / 10000 / current : 0, currency)}
                  text="average value"
                />
              </>
            )*/}
          </div>
        </div>
      )}
    </div>,
    document.body,
  );
}

function TooltipRow({ icon: Icon, iconClassName, value, text }) {
  return (
    <div className="flex items-start border-b border-dashed border-mystic-500 py-2 last:border-b-0">
      <Icon className={classNames('w-3.5 h-3.5 fill-current text-lynch-500 mr-1.5 shrink-0', iconClassName)} />
      <div>
        <span className="font-bold text-dodger-blue-500">{value}</span> {text}
      </div>
    </div>
  );
}
